import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { VictoryLabel, VictoryPie } from 'victory';
import { EcoDistributionDashboardDto } from 'api/dashboard/dtos/EcoDistributionDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import useElementSize from 'common/hooks/useElementSize';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import { FiltersFormModel } from '../filters/FiltersView';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import WidgetNoData from '../components/WidgetNoData';
import { ECO_SAFE_COLOR, ECO_WARNING_COLOR, ECO_DANGER_COLOR } from '../utils/ecoShared';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import styles from './EcoDistribution.module.scss';

type EcoBucket = 'ecological' | 'warning' | 'dangerous';

const BUCKETS: { key: EcoBucket; color: string; labelKey: string }[] = [
	{ key: 'ecological', color: ECO_SAFE_COLOR, labelKey: 'dashboard.eco_distribution_ecological' },
	{ key: 'warning', color: ECO_WARNING_COLOR, labelKey: 'dashboard.eco_distribution_warning' },
	{ key: 'dangerous', color: ECO_DANGER_COLOR, labelKey: 'dashboard.eco_distribution_dangerous' },
];

type Props = {
	filters?: FiltersFormModel;
};

const EcoDistribution = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<EcoDistributionDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [containerRef, { width, height }] = useElementSize();
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('ecoDistribution');
	const [tooltip, setTooltip] = useState<{ name: string; value: number; color: string; clientX: number; clientY: number } | null>(null);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters ? { ...props.filters } : { dateFrom, dateTo };
				const result = await DashboardService.getEcoDistribution(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const total = data?.total ?? 0;
	const allItems = BUCKETS
		.map(b => ({ ...b, value: data ? data[b.key] : 0 }))
		.filter(b => b.value > 0);
	const colorScale = allItems.map(b => b.color);

	const emptyState = !isLoading && total === 0;

	const isHorizontal = width > height * 1.3;
	const legendWidth = isHorizontal ? Math.min(width * 0.38, 160) : 0;
	const donutAreaWidth = isHorizontal ? width - legendWidth : width;
	const donutAreaHeight = isHorizontal ? height : Math.max(height - 30, 150);
	const donutSize = Math.min(donutAreaWidth, donutAreaHeight);
	const pieRadius = donutSize * 0.44;
	const pieInnerRadius = pieRadius * 0.7467;

	const pct = (value: number) => (total > 0 ? Math.round((value / total) * 100) : 0);

	// eslint-disable-next-line
	const pieEvents: any[] = [
		{
			target: 'data',
			eventHandlers: {
				onMouseEnter: (evt: any, targetProps: any) => {
					const datum = targetProps.datum;
					setTooltip({
						name: t(`dashboard.eco_distribution_${datum.key}` as any) as string,
						value: datum.value,
						color: datum.color,
						clientX: evt.clientX,
						clientY: evt.clientY,
					});
					return [];
				},
				onMouseMove: (evt: any) => {
					setTooltip(prev => prev ? { ...prev, clientX: evt.clientX, clientY: evt.clientY } : null);
					return [];
				},
				onMouseLeave: () => { setTooltip(null); return []; },
			},
		},
	];

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.eco_distribution') as string}
			subTitleTooltip={t('dashboard.eco_distribution_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			<div className={styles.chartContainer} ref={containerRef}>
				{isLoading ? (
					<div className={styles.centerAlign}><LoadingSpinner /></div>
				) : emptyState ? (
					<WidgetNoData />
				) : (
					width > 0 && height > 0 && (
						<div className={isHorizontal ? styles.layoutHorizontal : styles.layoutVertical}>
							<div className={styles.donutWrapper} style={{ width: donutAreaWidth, height: donutAreaHeight }}>
								<svg width={donutAreaWidth} height={donutAreaHeight}>
									<VictoryPie
										standalone={false}
										colorScale={colorScale}
										innerRadius={pieInnerRadius}
										radius={pieRadius}
										width={donutAreaWidth}
										height={donutAreaHeight}
										data={allItems}
										x="key"
										y="value"
										events={pieEvents}
										style={{ data: { cursor: 'pointer', stroke: '#fff', strokeWidth: 1 } }}
										labelComponent={<VictoryLabel text="" />}
									/>
								</svg>
								<div className={styles.centerLabel} style={{ maxWidth: pieInnerRadius * 1.7 }}>
									<div className={styles.centerLabelValue}>{total}</div>
									<div className={styles.centerLabelSub}>{t('dashboard.eco_distribution_trips') as string}</div>
								</div>
							</div>
							<div className={isHorizontal ? styles.htmlLegendRight : styles.htmlLegend}>
								{BUCKETS.map(b => {
									const value = data ? data[b.key] : 0;
									return (
										<span key={b.key} className={styles.legendItem}>
											<span className={styles.legendDot} style={{ backgroundColor: b.color }} />
											<span className={styles.legendName}>{t(b.labelKey as any) as string}</span>
											<span className={styles.legendPct}>{pct(value)}% · {value}</span>
										</span>
									);
								})}
							</div>
						</div>
					)
				)}

				{tooltip && (
					<ChartTooltipPortal clientX={tooltip.clientX} clientY={tooltip.clientY} className={styles.tooltip}>
						<div className={styles.tooltipRow}>
							<span className={styles.tooltipDot} style={{ backgroundColor: tooltip.color }} />
							<span>{tooltip.name}</span>
						</div>
						<div className={styles.tooltipValue}>{tooltip.value} ({pct(tooltip.value)}%)</div>
					</ChartTooltipPortal>
				)}
			</div>
		</Box>
	);
};

export default EcoDistribution;
